// HexagonChart
import { HexagonChartOption } from '@/common/components/molecules/hexagonChart/hexagonChart.types';
import { DB_TYPE, OS_TYPE } from '@/common/define/common.define';

export const DEGREE_60 = Math.PI / 3;
export const COS_60 = Math.cos(DEGREE_60);
export const SIN_60 = Math.sin(DEGREE_60);
export const GROUP_TITLE_IMAGE_SIZE = 20;
export const GROUP_TITLE_IMAGE_GAP = 5;
export const GROUP_BADGE_SIZE = 24;
export const GROUP_BADGE_PADDING = 5;
export const ELLIPSIS = '...';
export const ELLIPSIS_WIDTH = 12;
export const HEXAGON_LINE_WIDTH_MIN = 4;
export const HEXAGON_LINE_WIDTH_MAX = 10;
// maxSize 미지정(fill 모드) 시 테두리 두께 스케일 기준 반지름 — 기존 기본 maxSize(90)와 동일한 두께 유지
export const HEXAGON_LINE_WIDTH_REF_RADIUS = 90;
// 타일 텍스트 멀티라인 줄 간격(px) — 폰트 피팅 계산과 렌더링이 동일 값을 사용해야 함
export const HEXAGON_TEXT_LINE_GAP = 3;
export const LEGEND_DIMMED_ALPHA = 0.19;
// Hex alpha suffix derived from LEGEND_DIMMED_ALPHA — keeps both paths in sync
export const LEGEND_DIMMED_ALPHA_HEX = Math.round(LEGEND_DIMMED_ALPHA * 255)
  .toString(16)
  .padStart(2, '0');

export const HEXAGON_COLOR_TYPE = {
  widget: {
    running: '#4EFFBB',
    pending: '#FFD759',
    failed: '#FF3D70',
    succeeded: '#45A4FF',
    unknown: '#939393',
    disabled: '#3A3A3A',
  },
  infrastructure: {
    active: '#198FFF',
    inactive: '#A2A2A2',
  },
  kafka: {
    active: '#198FFF',
    inactive: '#A2A2A2',
  },
  instance: {
    total: '#FFFFFF',
    normal: '#12A480',
    warning: '#F9CB3B',
    critical: '#FF1F3F',
    noData: '#777777',
  },
  kubernetes: {
    total: '#FFFFFF',
    running: '#006DD6',
    succeeded: '#12A480',
    pending: '#FFD759',
    failed: '#FF1F3F',
    ready: '#12A480', // succeeded
    notReady: '#FFD759', // pending
    normal: '#12A480',
    warning: '#F9CB3B',
    critical: '#FF1F3F',
  },
  application: {
    total: '#FFFFFF',
    active: '#198FFF',
    inactive: '#A2A2A2',
  },
  cloud: {
    normal: '#10b981',
    warning: '#f59e0b',
    critical: '#ef4444',
  },
};
export const HEXAGON_OPTION: HexagonChartOption = {
  gap: 5,
  padding: 40,
  top: 20,
  minSize: 20,
  maxSize: 35,
  hideText: false,
  colorType: 'widget',
};

export const HEXAGON_TILE_MIN_SIZE = {
  small: 10,
  medium: 20,
  large: 30,
};

export const HEXAGON_IMAGE_URL = {
  [`${DB_TYPE.ORACLE}-mono`]: '/hexagon/icon-oracle-mono.svg',
  [`${DB_TYPE.ORACLE}-title`]: '/hexagon/icon-oracle-title.svg',
  [`${DB_TYPE.MYSQL}-mono`]: '/hexagon/icon-mysql-mono.svg',
  [`${DB_TYPE.MYSQL}-title`]: '/hexagon/icon-mysql-title.svg',
  'mysql-color': '/hexagon/icon-mysql-color.svg', // 비즈니스
  [`${DB_TYPE.POSTGRESQL}-mono`]: '/hexagon/icon-postgresql-mono.svg',
  [`${DB_TYPE.POSTGRESQL}-mono-standby`]: '/hexagon/icon-postgresql-standby.svg',
  [`${DB_TYPE.POSTGRESQL}-mono-primary`]: '/hexagon/icon-postgresql-primary.svg',
  [`${DB_TYPE.POSTGRESQL}-title`]: '/hexagon/icon-postgresql-title.svg',
  [`${DB_TYPE.SQLSERVER}-mono`]: '/hexagon/icon-sqlserver-mono.svg',
  [`${DB_TYPE.SQLSERVER}-title`]: '/hexagon/icon-sqlserver-title.svg',
  [`${DB_TYPE.REDIS}-mono`]: '/hexagon/icon-redis-mono.svg',
  [`${DB_TYPE.REDIS}-title`]: '/hexagon/icon-redis-title.svg',
  [`${DB_TYPE.CUBRID}-mono`]: '/hexagon/icon-cubrid-mono.svg',
  [`${DB_TYPE.CUBRID}-title`]: '/hexagon/icon-cubrid-title.svg',
  [`${DB_TYPE.MONGODB}-mono`]: '/hexagon/icon-mongodb-mono.svg',
  [`${DB_TYPE.MONGODB}-title`]: '/hexagon/icon-mongodb-title.svg',
  [`${DB_TYPE.ALTIBASE}-mono`]: '/hexagon/icon-altibase-mono.svg',
  [`${DB_TYPE.ALTIBASE}-title`]: '/hexagon/icon-altibase-title.svg',
  [`${DB_TYPE.TIBERO}-mono`]: '/hexagon/icon-tibero-mono.svg',
  [`${DB_TYPE.TIBERO}-title`]: '/hexagon/icon-tibero-title.svg',
  [`${DB_TYPE.CLICKHOUSE}-mono`]: '/hexagon/icon-clickhouse-mono.svg',
  [`${DB_TYPE.CLICKHOUSE}-title`]: '/hexagon/icon-clickhouse-title.svg',
  [`${DB_TYPE.REDSHIFT}-mono`]: '/hexagon/icon-redshift-mono.svg',
  [`${DB_TYPE.REDSHIFT}-title`]: '/hexagon/icon-redshift-title.svg',
  [`${DB_TYPE.SINGLESTORE}-mono`]: '/hexagon/icon-singlestore-mono.svg',
  [`${DB_TYPE.SINGLESTORE}-title`]: '/hexagon/icon-singlestore-title.svg',
  redshift: '/hexagon/icon-redshift-title.svg', // 비즈니스
  clickhouse: '/hexagon/icon-clickhouse-title.svg', // 비즈니스
  singlestore: '/hexagon/icon-singlestore-title.svg', // 비즈니스

  // OS Type Icon (Host View)
  [`${OS_TYPE.WINDOWS}-mono`]: '/hexagon/icon-windows-mono.svg',
  [`${OS_TYPE.WINDOWS}-title`]: '/hexagon/icon-windows-title.svg',
  [`${OS_TYPE.AIX}-mono`]: '/hexagon/icon-aix-mono.svg',
  [`${OS_TYPE.AIX}-title`]: '/hexagon/icon-aix-title.svg',
  [`${OS_TYPE.HP_UX}-mono`]: '/hexagon/icon-hp-mono.svg',
  [`${OS_TYPE.HP_UX}-title`]: '/hexagon/icon-hp-title.svg',
  [`${OS_TYPE.CENTOS}-mono`]: '/hexagon/icon-centos-mono.svg',
  [`${OS_TYPE.REDHAT}-mono`]: '/hexagon/icon-redhat-mono.svg',
  [`${OS_TYPE.ROCKY}-mono`]: '/hexagon/icon-rocky-mono.svg',
  [`${OS_TYPE.UBUNTU}-mono`]: '/hexagon/icon-ubuntu-mono.svg',
  [`${OS_TYPE.DEBIAN}-mono`]: '/hexagon/icon-debian.mono.svg',
  [`${OS_TYPE.SUSE}-mono`]: '/hexagon/icon-suse-mono.svg',
  [`${OS_TYPE.FEDORA}-mono`]: '/hexagon/icon-fedora-mono.svg',
  [`${OS_TYPE.UNKNOWN}-mono`]: '/hexagon/icon-linux-mono.svg',
  [`${OS_TYPE.UNKNOWN}-title`]: '/hexagon/icon-linux-title.svg',
  [`${OS_TYPE.Linux}-title`]: '/hexagon/icon-linux-title.svg',
  [`${OS_TYPE.SUNOS}-mono`]: '/hexagon/icon-sunos-mono.svg',
  [`${OS_TYPE.SUNOS}-title`]: '/hexagon/icon-sunos-title.svg',

  // 대시보드
  // Application
  'java-mono': '/hexagon/icon-java-mono.svg',
  'python-mono': '/hexagon/icon-python-mono.svg',
  'tomcat-mono': '/hexagon/icon-tomcat-mono.svg',
  'apache-mono': '/hexagon/icon-apache-mono.svg',
  'dotnet-mono': '/hexagon/icon-dotnet-mono.svg',
  'dotnet_framework-mono': '/hexagon/icon-dotnet-framework-mono.svg',
  'nodejs-mono': '/hexagon/icon-nodejs-mono.svg',
  // Node
  'node-mono': '/hexagon/icon-node-mono.svg',
  // Pod
  'pod-mono': '/hexagon/icon-pod-mono.svg',
  // Container
  'docker-mono': '/hexagon/icon-docker-mono.svg',
  'kubernetes-mono': '/hexagon/icon-kubernetes-mono.svg',
  // Network Device
  'os-mono': '/hexagon/icon-os-mono.svg', // TODO: 정해진 게 없어서 Desktop 이미지로 넣었음.
  'fortigate-mono': '/hexagon/icon-fortigate-mono.svg',
  'aruba-mono': '/hexagon/icon-aruba-mono.svg',
  'hp-mono': '/hexagon/icon-hp-mono.svg',
  'ingress-mono': '/hexagon/icon-ingress-mono.svg',
  'ingress-controller-mono': '/hexagon/icon-ingress-mono.svg',
  // AWS (Cloud Service 공용)
  'aws_documentdb-mono': '/hexagon/icon-aws_documentdb-mono.svg',
  'aws_dynamodb-mono': '/hexagon/icon-aws_dynamodb-mono.svg',
  'aws_elasticache_cluster-mono': '/hexagon/icon-aws_elasticache-mono.svg',
  'aws_elasticache_shard-mono': '/hexagon/icon-aws_elasticache-mono.svg',
  'aws_elasticache_node-mono': '/hexagon/icon-aws_elasticache-mono.svg',
  'aws_elasticache_serverless-mono': '/hexagon/icon-aws_elasticache-mono.svg',
  'aws_redshift-mono': '/hexagon/icon-aws_redshift-mono.svg',
  'aws_ec2-mono': '/hexagon/icon-aws-ec2-mono.svg',
  'aws_ecs_service-mono': '/hexagon/icon-aws-ecs-mono.svg',
  'aws_ecs_cluster-mono': '/hexagon/icon-aws-ecs-mono.svg',
  'aws_lambda-mono': '/hexagon/icon-aws-lambda-mono.svg',
  'aws_s3-mono': '/hexagon/icon-aws-s3-mono.svg',
  'aws_rds-mono': '/hexagon/icon-aws-rds-mono.svg',
  'aws_dms-mono': '/hexagon/icon-aws-dms-mono.svg',
  'aws_dms_replinstance-mono': '/hexagon/icon-aws-dms-mono.svg',
  'aws_dms_task-mono': '/hexagon/icon-aws-dms-mono.svg',
  // AZURE (Cloud Service 공용)
  'azure_virtual_machines-mono': '/hexagon/icon-azure-virtual-machines-mono.svg',
  'azure_functions-mono': '/hexagon/icon-azure-functions-mono.svg',
  'azure_blob_storage-mono': '/hexagon/icon-azure-blob-storage-mono.svg',

  'azure_sql_database-mono': '/hexagon/icon-azure-sql-database-mono.svg',
  'azure_database_for_mysql-mono': '/hexagon/icon-azure-mysql-mono.svg',
  'azure_database_for_postgresql-mono': '/hexagon/icon-azure-postgresql-mono.svg',
  'azure_mariadb-mono': '/hexagon/icon-azure-mariadb-mono.svg',
  'azure_cosmos_db-mono': '/hexagon/icon-azure-cosmos-db-mono.svg',
  'azure_cache_for_redis-mono': '/hexagon/icon-azure-cache-for-redis-mono.svg',
  'azure_cache_for_redis_enterprise-mono':
    '/hexagon/icon-azure-cache-for-redis-enterprise-mono.svg',
  // Kafka
  'kafka-mono': '/hexagon/icon-kafka-mono.svg',
  'kafka_broker-mono': '/hexagon/icon-kafka-mono.svg',
  'kafka_cluster_consumer_group-mono': '/hexagon/icon-kafka-mono.svg',
  'kafka_active_controller-mono': '/hexagon/icon-active-controller.svg',

  // 기본뷰 > ~~ 그룹별로 묶었을 때
  'hostGroup-title': '/hexagon/icon-host-title.svg',
  'wasGroup-title': '/hexagon/icon-was-title.svg',
  'python-title': '/hexagon/icon-python-title.svg',
  'java-title': '/hexagon/icon-java-title.svg',
  'nodejs-title': '/hexagon/icon-nodejs-title.svg',
  'dotnet-title': '/hexagon/icon-dotnet-title.svg',
  'dotnet_framework-title': '/hexagon/icon-dotnet-framework-title.svg',
  'cluster-title': '/hexagon/icon-cluster-title.svg',
  'zookeeper-title': '/hexagon/icon-zookeeper-title.svg',

  // 알람 아이콘
  'alert-normal-mono': '/hexagon/icon-alert-normal.svg',
  'alert-warning-mono': '/hexagon/icon-alert-warning.svg',
  'alert-critical-mono': '/hexagon/icon-alert-critical.svg',

  // 제품연계
  'trino-mono': '/hexagon/icon-trino-mono.svg',
};

export const DEFAULT_STATUS_LEVEL = {
  pending: 4,
  critical: 4,
  fail: 4,
  failed: 3,
  inactive: 3,
  abnormal: 3,
  terminated: 3,
  warning: 3,
  unknown: 2,
  running: 1,
  normal: 0,
  active: 0,
  succeeded: 0,
  // noStatus는 상태가 아니므로 심각순 정렬 시 항상 맨 뒤로 간다.
  noStatus: -1,
} as const;
